import { Gift } from "lucide-react";
import {
  Create,
  Datagrid,
  DateField,
  DateInput,
  Edit,
  Labeled,
  List,
  useInput,
  required,
  NumberField,
  SimpleForm,
  TextField,
  useRecordContext,
  RadioButtonGroupInput,
  TextInput,
  useNotify,
  useRedirect,
  useRefresh,
  ReferenceField,
} from "react-admin";
import { useController } from "react-hook-form";
import { CardListActions, EditorToolbar, validation } from "../../components";
import DeleteIcon from "@mui/icons-material/Delete";
import Icon from "@mui/material/Icon";
import { useState } from "react";

const filters = [
  <NumberField source="id" label="Id" alwaysOn key="id" />,
  <DateInput source="date" label="Dedication Date" alwaysOn key="date" />,
];
const DateRendorField = ({ source }) => {
  const record = useRecordContext(); // Get current record context
  if (!record) return null;

  // Extract the date and format it
  const dateSource = record[source];
  let dateString = dateSource.replace("T00:00:00.000Z", "");
  const [year, month, day] = dateString.split("-");
  return `${month}/${day}/${year}`;
};
const PageDescription = () => (
  <div className="page-description">
    <h1>Shoutout Dedication</h1>
    <p>
      This page displays list of all shoutout dedications that appear before
      videos on the platform. Each dedication includes details like the
      speaker's ID and the dedication amount.
    </p>
  </div>
);
const AddEditPageDescription = () => (
  <div className="page-description">
    <h1>Add or Edit Shoutout Dedication</h1>
    <p>
      Use this page to create a new shoutout dedication or update an existing
      one. Provide the required details, such as speaker ID, dedication amount,
      and dedication status, to ensure the dedication is played accurately
      before videos.
    </p>
  </div>
);
function ListView(props) {
  return (
    <>
      <PageDescription />
      <List
        {...props}
        actions={<CardListActions exporter={true} />}
        filters={filters}
        perPage={50}
        sort={{ field: "created_at", order: "DESC" }}
        title="Shoutout Dedication"
      >
        <Datagrid rowClick="edit">
          <TextField
            source="id"
            label="ID"
            validate={[validation.required()]}
          />
          <ReferenceField
            source="speaker_id"
            reference="speakers"
            label="Speaker"
          >
            <TextField source="name" />
          </ReferenceField>

          <TextField source="email" label="User Email" />
          <TextField source="name_first" label="First Name" />
          <TextField source="name_last" label="Last Name" />
          <TextField source="message" label="Shoutout Message" />
          <TextField source="speaker_notice" label="Speaker Notice" />
          <DateRendorField source="date_from" label="Dedication Start Date" />
          <DateRendorField source="date_to" label="Dedication Date" />
          <TextField source="status" label="Dedication Status" />
        </Datagrid>
      </List>
    </>
  );
}
const SponsorNameField = () => {
  const { field: dedicationBy } = useController({
    name: "anonymous",
    defaultValue: "",
  });
  const { field: dedicationSponsor } = useController({
    name: "sponsor_name",
    defaultValue: "",
  });
  return (
    dedicationBy &&
    dedicationBy.value == 1 && (
      <TextInput source="sponsor_name" label="Sponsor Name" />
    )
  );
};

const RenderEditForm = (redirect) => {
  return (
    <SimpleForm redirect={redirect} toolbar={<EditorToolbar />}>
      <AddEditPageDescription />
      {/* <TextInput source="id" label="ID" validate={[validation.required()]} /> */}
      <ReferenceField source="speaker_id" reference="speakers" label="Speaker">
        <Labeled>
          <TextField source="name" label="Speaker Name" />
        </Labeled>
      </ReferenceField>
      <Labeled>
        <TextField source="email" label="Email" />
      </Labeled>
      <Labeled>
        <TextField source="name_first" label="First Name" />
      </Labeled>
      <Labeled>
        <TextField source="name_last" label="Last Name" />
      </Labeled>
      <Labeled>
        <TextField source="message" label="Shoutout Message" />
      </Labeled>
      <Labeled>
        <TextField source="speaker_notice" label="Speaker Notice" />
      </Labeled>
      <Labeled>
        <DateRendorField source="date_from" label="Dedication Start Date" />
        {/* <DateInput source="date_from" label="Dedication Start Date" /> */}
      </Labeled>
      <Labeled>
        <DateRendorField source="date_to" label="Dedication End Date" />
        {/* <DateInput source="date_to" label="Dedication End Date" /> */}
      </Labeled>
      <RadioButtonGroupInput
        source="status"
        label="Dedication Status"
        choices={[
          { id: "pending", name: "Pending" },
          { id: "completed", name: "Completed" },
          { id: "cancelled", name: "Cancelled" },
        ]}
        validate={[required()]}
      />
    </SimpleForm>
  );
};

function EditView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Edit
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Dedication payout updated successfully!", {
            type: "success",
          });
          refresh(); // Ensures UI updates with the new data
          redirect(false);
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {/* <Edit {...props} undoable={false}> */}
      {RenderEditForm({ redirect: false })}
    </Edit>
  );
}
function CreateView(props) {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  return (
    <Create
      {...props}
      mutationMode="pessimistic"
      mutationOptions={{
        onSuccess: () => {
          notify("Dedication created successfully!", { type: "success" });
          refresh(); // Ensures UI updates with the new data
          redirect("list", "sponsor_dedications"); // Redirect back to list after update
        },
        onError: (error) => {
          notify(`Error: ${error.message}`, { type: "error" });
        },
      }}
    >
      {RenderEditForm({ redirect: "edit" })}
    </Create>
  );
}
export const ShoutoutDedications = {
  category: "database",
  name: "shoutout_dedications",
  icon: Gift,
  list: ListView,
  edit: EditView,
  create: CreateView,
  options: { label: "Shoutout Dedication" },
};
